import range from 'zycommon-web/utils/range';

/* eslint-disable no-magic-numbers */
const prefixAbbreviationNameExponent = [
    // Rare units in engineering
    ['Q', 'quetta', 30],
    ['R', 'ronna', 27],
    ['Y', 'yotta', 24],
    ['Z', 'zetta', 21],
    ['E', 'exa', 18],
    ['P', 'peta', 15],
    ['T', 'tera', 12],

    // Common engineering notation units
    ['G', 'giga', 9],
    ['M', 'mega', 6],
    ['k', 'kilo', 3],

    // Not engineering notation b/c not multiple of 3
    ['h', 'hecto', 2],
    ['da', 'deka', 1],
    ['d', 'deci', -1],
    ['c', 'centi', -2],

    // Common engineering notation units
    ['m', 'milli', -3],
    ['μ', 'micro', -6],
    ['n', 'nano', -9],
    ['p', 'pico', -12],

    // Rare units in engineering
    ['f', 'femto', -15],
    ['a', 'atto', -18],
    ['z', 'zepto', -21],
    ['y', 'yocto', -24],
    ['r', 'ronto', -27],
    ['q', 'quecto', -30],
];
/* eslint-enable no-magic-numbers */

// Chosen units from https://www.nist.gov/sites/default/files/styles/2800_x_2800_limit/public/images/2021/08/23/NIST.SP_.1247.png?itok=CAFgVO0D
const physicalQuantityAbbreviationName = [
    // Base quantities
    ['s', 'second'],
    ['m', 'meter'],
    ['g', 'gram'],
    ['A', 'ampere'],
    ['K', 'kelvin'],
    ['mol', 'mole'],
    ['cd', 'candela'],

    // Derived quantities
    ['J', 'joule'],
    ['N', 'newton'],
    ['C', 'coulomb'],
    ['W', 'watt'],
    ['Pa', 'pascal'],
    ['V', 'volt'],
    ['Wb', 'weber'],
    ['T', 'tesla'],
    ['Hz', 'hertz'],
    ['H', 'henry'],
    ['F', 'farad'],
    ['S', 'siemens'],
    ['rad', 'radian'],
    ['kat', 'katal'],
    ['lx', 'lux'],
    ['lm', 'lumen'],
    ['Bq', 'becquerel'],
    ['Gy', 'gray'],
    ['sr', 'steradian'],
    ['Sv', 'sievert'],
    ['Ω', 'ohm'],
    ['°C', 'degree Celsius'],

    // Imperial units
    ['in', 'inch'],
    ['ft', 'foot'],
    ['yd', 'yard'],
    ['mi', 'mile'],
    ['qt', 'quart'],
    ['gal', 'gallon'],
    ['lb', 'pound'],
    ['ton', 'ton'],
    ['Btu', 'British thermal unit'],
    ['hp', 'horsepower'],
    ['sec', 'second'],
    ['min', 'minute'],
    ['hr', 'hour'],
    ['yr', 'year'],
];

/* eslint-disable id-length */
const symbolStringToCommandEnding = {
    // Testing if the below line is needed
    // r: 'ro',
    μ: 'mu',
    Ω: 'ohm',
    '°C': 'celsius',
};
const symbolStringToLatex = {
    μ: '\\mu',
    Ω: '\\Omega',
    '°C': '^{\\circ}\\mathrm{C}',
};
const symbolStringToKey = {
    μ: 'u',
    Ω: 'o',
    '°C': 'C',
};

// MathJS prefixes: https://mathjs.org/docs/datatypes/units.html#prefixes
// MathJS physical quantities: https://mathjs.org/docs/datatypes/units.html#reference
const symbolStringToMathJS = {
    rad: 'radian',
    μ: 'u',
    Ω: 'ohm',
    '°C': 'degC',
};
export const physicalQuantityStringToKeycapAside = {
    s: 'Second',
    m: 'Meter',
    g: 'Gram',
    A: 'Ampere',
    K: 'Kelvin',
    mol: 'Mole',
    cd: 'Candela',
    J: 'Joule',
    N: 'Newton',
    C: 'Coulomb',
    W: 'Watt',
    Pa: 'Pascal',
    V: 'Volt',
    Wb: 'Weber',
    T: 'Tesla',
    Hz: 'Hertz',
    H: 'Henry',
    F: 'Farad',
    S: 'Siemens',
    rad: 'Radian',
    kat: 'Katal',
    lx: 'Lux',
    lm: 'Lumen',
    Bq: 'Becquerel',
    Gy: 'Gray',
    sr: 'Steradian',
    Sv: 'Sievert',
    Ω: 'Ohm',
    '°C': 'Celsius',
    in: 'inch',
    ft: 'foot',
    yd: 'yard',
    mi: 'mile',
    qt: 'quart',
    gal: 'gallon',
    lb: 'pound',
    ton: 'ton',
    Btu: 'British thermal unit',
    hp: 'horsepower',
    sec: 'second',
    min: 'minute',
    hr: 'hour',
    yr: 'year',
};
/* eslint-enable id-length */

/**
 * A unit prefix (like m in milli) or physical quantity (like V in Volts).
 * @class EngineeringSymbol
 */
class EngineeringSymbol {
    /**
     * The abbreviation of this symbol. Ex: m for milli.
     * @type {string}
     */
    abbreviation = null;

    /**
     * The name of the symbol. Ex: milli.
     * @type {string}
     * @default ''
     */
    name = '';

    /**
     * The type of symbol.
     * @type {string}
     * @default 'S'
     */
    type = 'S';

    /**
     * The symbol's macro command.
     * @type {string}
     */
    get command() {
        return this.type + this.commandEnding;
    }

    /**
     * The ending of the command.
     * @type {string}
     */
    get commandEnding() {
        return symbolStringToCommandEnding[this.abbreviation] ?? this.abbreviation;
    }

    /**
     * The symbol's latex.
     * @type {string}
     */
    get latex() {
        return symbolStringToLatex[this.abbreviation] ?? `\\mathrm{${this.abbreviation}}`;
    }

    /**
     * The key to bind to this symbol.
     * @type {string}
     */
    get key() {
        return symbolStringToKey[this.abbreviation] ?? this.abbreviation[0];
    }

    /**
     * This symbol in MathJS notation.
     * @type {string}
     */
    get mathjs() {
        return symbolStringToMathJS[this.abbreviation] ?? this.abbreviation;
    }

    /**
     * The constructor for this component.
     * @param {string} abbreviation The abbreviation of this symbol.
     */
    constructor(abbreviation) {
        this.abbreviation = abbreviation;
    }
}

/**
 * A unit prefix like m in milli.
 * @class UnitPrefix
 * @extends EngineeringSymbol
 */
export class UnitPrefix extends EngineeringSymbol {
    /**
     * The symbol's value as an exponent in 10^{exponent}.
     * @type {number}
     */
    exponent = null;

    /**
     * The type of symbol.
     * @type {string}
     * @default 'P'
     */
    type = 'P';

    /**
     * The constructor for this component.
     * @param {string} abbreviation The abbreviation of this symbol.
     * @param {string} name The name of this symbol.
     * @param {number} exponent The symbol's value as an exponent in 10^{exponent}.
     */
    constructor(abbreviation, name, exponent) {
        super(abbreviation);
        this.name = name;
        this.exponent = exponent;
    }
}

/**
 * A physical quantity in a unit, like V in Volts.
 * @class PhysicalQuantity
 * @extends EngineeringSymbol
 */
class PhysicalQuantity extends EngineeringSymbol {
    /**
     * The type of symbol.
     * @type {string}
     * @default 'Q'
     */
    type = 'Q';

    /**
     * The constructor for this component.
     * @param {string} abbreviation The abbreviation of this symbol.
     * @param {string} name The name of this symbol.
     */
    constructor(abbreviation, name) {
        super(abbreviation);
        this.name = name;
    }
}

// Build a generic symbol for each letter, upper and lower case.
const upperCaseCharCode = 65;
const numLetters = 26;
const uppercaseLetters = range(upperCaseCharCode, upperCaseCharCode + numLetters).map(charCode => String.fromCharCode(charCode));
const letters = [...uppercaseLetters, ...uppercaseLetters.map(letter => letter.toLowerCase())];
export const genericSymbols = letters.map(letter => new EngineeringSymbol(letter));

export const physicalQuantities = physicalQuantityAbbreviationName
    .map(quantity => new PhysicalQuantity(...quantity))
    .sort((first, second) => first.abbreviation.localeCompare(second.abbreviation));
export const unitPrefixes = prefixAbbreviationNameExponent.map(prefix => new UnitPrefix(...prefix));
export const engineeringSymbols = [...genericSymbols, ...physicalQuantities, ...unitPrefixes];
export const commandToLatex = Object.fromEntries(engineeringSymbols.map(({ command, latex }) => [command, latex]));

/**
 * Units may start with parens or 1/, then be followed by an engineering symbol.
 * @constant startOfUnitsRegex
 * @type {RegEx}
 */
export const startOfUnitsRegex = /(\(|1\/)*\\(P|Q|S)[a-zA-Z]+/u;

/**
 * Replace each engineering symbol in the given string with that symbol's LaTeX command.
 * @function replaceEngineeringSymbolCommands
 * @param {string} string The given string.
 * @returns {string}
 */
export function replaceEngineeringSymbolCommands(string) {
    // Some commands have a superset of the characters that another command has. Ex: \Qmu is a superset of \Qm.
    // So, the superset command needs to be replaced first.
    // Thus, sort in reverse.
    return [...engineeringSymbols, { command: 'UP', latex: '{-}' }]
        .sort((first, second) => first.command.localeCompare(second.command))
        .reverse()
        .reduce((acc, { command, latex }) => acc.replaceAll(`\\${command}`, latex), string);
}

/**
 * Replace each engineering symbol in the given string with English. Ex: mm -> millimeter.
 * @param {string} string The string to convert.
 * @returns {string}
 */
export function replaceEngineeringSymbolsWithEnglish(string) {
    const inEnglish = [...physicalQuantities, ...unitPrefixes]
        .sort((first, second) => first.command.localeCompare(second.command))
        .reverse()
        .reduce((acc, { command, name }) => acc.replaceAll(`\\${command}`, `\\mathrm{${name}}`), string);

    return replaceEngineeringSymbolCommands(inEnglish);
}

/**
 * Replace each unicode char in the given string with that symbol's LaTeX command.
 * @function replaceUnicodeWithLatex
 * @param {string} string The given string.
 * @returns {string}
 */
export function replaceUnicodeWithLatex(string) {
    return Object.entries(symbolStringToLatex).reduce((acc, [unicode, latex]) => acc.replaceAll(unicode, latex), string);
}

/**
 * Get the unit product symbol in the given latex.
 * @function unitsProductSymbol
 * @param {string} latex The given latex.
 * @returns {string}
 */
function unitsProductSymbol(latex) {
    const match = latex.match(startOfUnitsRegex);

    if (match) {
        const units = latex.slice(match.index);

        if (units.includes('UP')) {
            return '\\UP';
        } else if (units.includes('cdot')) {
            return '\\cdot';
        }
    }

    return null;
}

/**
 * Choose a unit product symbol from the student's answer. If not one, choose from the expected answer. If not one, use \cdot.
 * @function chooseFamiliarUnitProductSymbol
 * @param {string} actual The submitted answer.
 * @param {string} expected The expected answer.
 * @returns {string}
 */
export function chooseFamiliarUnitProductSymbol({ actual, expected }) {
    return unitsProductSymbol(actual) ?? unitsProductSymbol(expected) ?? '\\cdot';
}
